import { useState } from 'react';
import {
  REFUND_REASON_CATEGORIES,
  type FinanceOrderDetail,
  type FinanceOrderTicket,
  type RefundReasonCategory,
  type RefundView,
} from '@gatecheckpro/contracts';
import {
  REFUND_STATES,
  Button,
  Card,
  LoadingRegion,
  Notice,
  Status,
  formatMoney,
  presentation,
} from '@gatecheckpro/design-system';
import { useResource } from '../../lib/useResource.ts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * One order, and the refund raised against it.
 *
 * Every figure and every eligibility decision here comes from the server. The
 * tickets are the authoritative records from the same read the refund command
 * validates against, so what this offers and what the command accepts cannot
 * drift apart. `refundableMinor` and `alreadyClaimedMinor` are printed as
 * returned — a surface that recomputed the over-refund rule would eventually
 * disagree with the rule, and the disagreement would show up as a customer
 * being offered money the next call refuses.
 *
 * The only arithmetic in this file is adding up the amounts an operator has
 * typed, because the request schema requires the total to equal the sum of its
 * lines and refuses the mismatch. That is a form-consistency check on the
 * caller's own input, not a commercial judgement.
 */
export function OrderRefundPanel({
  base,
  orderId,
  onRaised,
}: {
  base: string;
  orderId: string;
  onRaised: (refund: RefundView) => void;
}): React.JSX.Element {
  const order = useResource<FinanceOrderDetail>(`${base}/finance/orders/${orderId}`);

  return (
    <>
      {order.resource.status === 'loading' && <LoadingRegion label="Loading the order" />}
      {order.resource.status === 'failed' && <FailureNotice error={order.resource.error} />}
      {order.resource.status === 'loaded' && (
        <RaiseAgainst
          base={base}
          order={order.resource.data}
          onRaised={(refund) => {
            order.reload();
            onRaised(refund);
          }}
        />
      )}
    </>
  );
}

function RaiseAgainst({
  base,
  order,
  onRaised,
}: {
  base: string;
  order: FinanceOrderDetail;
  onRaised: (refund: RefundView) => void;
}): React.JSX.Element {
  const raise = useCommand<RefundView>();
  const [chosen, setChosen] = useState<Record<string, number>>({});
  /*
   * One key per intent, not per click.
   *
   * This was a timestamp, which made every attempt a different request — so the
   * double-click an operator makes on a slow connection would have raised two
   * refunds against the same tickets, which is precisely what an idempotency
   * key exists to prevent. Generated once when the form appears and rotated
   * only after a refund is actually raised, so a retry of the same intent
   * carries the same key and a genuinely new refund carries a new one.
   */
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [reasonCategory, setReasonCategory] = useState<RefundReasonCategory>('CUSTOMER_REQUEST');
  const [reasonDetail, setReasonDetail] = useState('');

  const lines = Object.entries(chosen);
  const total = lines.reduce((sum, [, minor]) => sum + minor, 0);

  /**
   * Whether a ticket can be named on a refund at all.
   *
   * Both facts come from the server and neither is inferred: `alreadyClaimed`
   * is the precise predicate the refund command checks, so exactly the tickets
   * it would refuse are the ones disabled here — no more, which would hide a
   * legitimate refund, and no fewer, which would offer one that fails.
   */
  const claimable = (ticket: FinanceOrderTicket): boolean =>
    ticket.status === 'ISSUED' && !ticket.alreadyClaimed;

  return (
    <Card title={`Order ${order.orderNumber}`} eyebrow={order.eventTitle}>
      <div className="gc-stack">
        <div className="gc-summary__row">
          <span>Collected</span>
          <span className="gc-summary__value gc-mono">{formatMoney(order.amount)}</span>
        </div>
        <div className="gc-summary__row" data-testid="order-refundable">
          <span>Still refundable</span>
          <span className="gc-summary__value gc-mono">
            {/* Printed, never derived. See the note at the top of this file. */}
            {formatMoney({ minor: order.refundableMinor, currency: order.amount.currency })}
          </span>
        </div>
        {order.alreadyClaimedMinor > 0 && (
          <div className="gc-summary__row" data-testid="order-already-claimed">
            <span>Already refunded or promised back</span>
            <span className="gc-summary__value gc-mono">
              {formatMoney({ minor: order.alreadyClaimedMinor, currency: order.amount.currency })}
            </span>
          </div>
        )}

        {order.refunds.length > 0 && (
          <div className="gc-stack gc-stack--tight" data-testid="order-refunds">
            <strong>Refunds already raised</strong>
            {order.refunds.map((refund) => {
              const state = presentation(REFUND_STATES, refund.status);
              return (
                <div key={refund.refundId} className="gc-summary__row">
                  <span>{refund.refundNumber}</span>
                  <span className="gc-row">
                    <span className="gc-mono">{formatMoney(refund.amount)}</span>
                    <Status tone={state.tone} glyph={state.glyph} label={state.label} />
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <form
          className="gc-stack"
          onSubmit={(formEvent) => {
            formEvent.preventDefault();
            void raise
              .run(`${base}/orders/${order.orderId}/refunds`, {
                method: 'POST',
                body: {
                  // Assembled entirely from the finance read: these ids came
                  // from the order detail above and from nowhere else.
                  tickets: lines.map(([ticketId, amountMinor]) => ({ ticketId, amountMinor })),
                  amountMinor: total,
                  reasonCategory,
                  reasonDetail: reasonDetail.trim() === '' ? null : reasonDetail.trim(),
                  idempotencyKey,
                },
              })
              .then((refund) => {
                if (refund === null) return;
                setIdempotencyKey(crypto.randomUUID());
                onRaised(refund);
              });
          }}
        >
          <fieldset className="gc-fieldset">
            <legend className="gc-field__label">Which tickets</legend>
            {order.tickets.map((ticket) => (
              <label key={ticket.ticketId} className="gc-choice">
                <input
                  type="checkbox"
                  disabled={!claimable(ticket)}
                  checked={ticket.ticketId in chosen}
                  onChange={(changeEvent) => {
                    setChosen((current) => {
                      const next = { ...current };
                      if (changeEvent.target.checked) next[ticket.ticketId] = ticket.unitPriceMinor;
                      else delete next[ticket.ticketId];
                      return next;
                    });
                  }}
                />
                <span>
                  {ticket.ticketNumber} ·{' '}
                  {formatMoney({
                    minor: ticket.unitPriceMinor,
                    currency: order.amount.currency,
                  })}
                  {ticket.status === 'VOID' && (
                    <span className="gc-muted"> — already cancelled</span>
                  )}
                  {ticket.status !== 'VOID' && ticket.alreadyClaimed && (
                    <span className="gc-muted"> — already covered by a refund</span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>

          <label className="gc-field">
            <span className="gc-field__label">Why</span>
            <select
              className="gc-input"
              value={reasonCategory}
              onChange={(changeEvent) =>
                setReasonCategory(changeEvent.target.value as RefundReasonCategory)
              }
            >
              {REFUND_REASON_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category.replaceAll('_', ' ').toLowerCase()}
                </option>
              ))}
            </select>
          </label>

          <label className="gc-field">
            <span className="gc-field__label">Notes</span>
            <input
              className="gc-input"
              value={reasonDetail}
              onChange={(changeEvent) => setReasonDetail(changeEvent.target.value)}
            />
          </label>

          <div className="gc-summary__row">
            <span>Refund total</span>
            <span className="gc-summary__value gc-mono" data-testid="refund-total">
              {formatMoney({ minor: total, currency: order.amount.currency })}
            </span>
          </div>

          {/*
            Offered only when something is selected. Whether the amount is
            allowed is the server's answer, and it is not pre-empted here: an
            over-refund comes back as REFUND_EXCEEDS_COLLECTION with wording an
            operator can act on.
          */}
          <Button type="submit" variant="primary" busy={raise.busy} disabled={lines.length === 0}>
            Raise this refund
          </Button>

          <FailureNotice error={raise.error} />
        </form>

        {order.tickets.every((ticket) => !claimable(ticket)) && (
          <Notice tone="info" title="Nothing left to refund on this order">
            Every ticket is either already cancelled or already covered by a refund.
          </Notice>
        )}
      </div>
    </Card>
  );
}
